import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Divider,
  Alert,
  useTheme,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useQuery } from "@apollo/client";
import { GET_CUSTOMER_INTEGRATIONS } from "../../../../apis/settingsApis/integration-apis/integrationApis";
import AzureGuide from "../../../Organization/InfraManagement/InfraTemplateDialog/guides/AzureGuide";
import azureLogo from "../../../../Assets/Images/azure.png";

const AzureConnectionDrawer = ({
  open,
  onClose,
  refetchIntegrationDetails,
  onStoreIntegrationDetails,
}) => {
  const theme = useTheme();
  const [isIntegrationActive, setIsIntegrationActive] = useState(undefined);
  const [azureTenantId, setAzureTenantId] = useState("");
  const [azureSubscriptionId, setAzureSubscriptionId] = useState("");
  const [azureClientId, setAzureClientId] = useState("");
  const [azureClientSecret, setAzureClientSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const {
    data: customerIntegrationsResponse,
    loading: loadingCustomerIntegrations,
    refetch: refetchCustomerIntegrations,
  } = useQuery(GET_CUSTOMER_INTEGRATIONS);

  useEffect(() => {
    if (customerIntegrationsResponse) {
      const integration =
        customerIntegrationsResponse?.getAccountDetails?.userIntegrationsDetails?.list?.find(
          (item) => item.integration_key === "azure"
        );
      if (!integration) {
        setIsIntegrationActive(false);
      } else if (integration?.is_active) {
        setIsIntegrationActive(true);
      }
    }
  }, [customerIntegrationsResponse]);

  const validateFields = () => {
    return [
      azureTenantId,
      azureSubscriptionId,
      azureClientId,
      azureClientSecret,
    ].every((field) => field.trim().length > 0);
  };

  const handleSubmit = async () => {
    const integrationResourceId = JSON.stringify({
      tenant_id: azureTenantId.trim(),
      subscription_id: azureSubscriptionId.trim(),
      client_id: azureClientId.trim(),
      client_secret: azureClientSecret.trim(),
    });

    setSubmitting(true);
    try {
      const result = await onStoreIntegrationDetails({
        integratedAccountId: integrationResourceId,
        integrationKey: "azure",
        integrationType: "AZURE",
        sync: true,
      });

      if (result?.success) {
        onClose();
      }
    } catch (error) {
      console.error("Error updating integration details:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      TransitionComponent={useMemo(
        () => (props) => <Slide {...props} direction="left" />,
        []
      )}
      PaperProps={{
        sx: {
          width: "75%",
          maxWidth: "1400px",
          backgroundColor: theme.customColors.background.main,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#FFFFFF",
          borderBottom: `1px solid ${theme.customColors.border.subtle}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            py: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "10px",
                backgroundColor: theme.customColors.background.cardHeader,
                border: `1px solid ${theme.customColors.border.main}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={azureLogo}
                alt="Azure"
                style={{ width: 32, height: 32, objectFit: "contain" }}
              />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors.text.strong,
                  letterSpacing: "-0.02em",
                }}
              >
                Connect Microsoft Azure
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.customColors.text.secondary,
                  mt: 0.5,
                }}
              >
                Follow the steps below to establish a secure connection
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: theme.customColors.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 4,
          py: 3,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.customColors.border.main,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.customColors.border.strong,
          },
        }}
      >
        {/* Setup Guide Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: theme.customColors.text.strong,
            }}
          >
            Setup Guide
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors.text.secondary,
              mb: 1.5,
            }}
          >
            Complete the following steps in your Microsoft Azure account before
            entering your credentials
          </Typography>
          <Box
            sx={{
              mt: 1,
              px: 2,
              py: 1.5,
              backgroundColor: theme.customColors.background.info,
              border: `1px solid ${theme.customColors.border.subtle}`,
              borderRadius: "8px",
              mb: 3,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.secondary,
                lineHeight: 1.6,
                fontSize: "0.875rem",
              }}
            >
              <Box
                component="strong"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors.text.strong,
                }}

              >
                Note:
                If you're integrating your 2nd, 3rd, or nth account, you can directly skip to Step 6.
              </Box>{" "}
            </Typography>

          </Box>
          <AzureGuide />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Credentials Form Section */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: theme.customColors.text.strong,
            }}
          >
            Provide Your Credentials
          </Typography>
          <Grid container spacing={2}>
            {[
              {
                label: "Tenant ID",
                value: azureTenantId,
                setter: setAzureTenantId,
              },
              {
                label: "Subscription ID",
                value: azureSubscriptionId,
                setter: setAzureSubscriptionId,
              },
              {
                label: "Client ID",
                value: azureClientId,
                setter: setAzureClientId,
              },
              {
                label: "Client Secret",
                value: azureClientSecret,
                setter: setAzureClientSecret,
              },
            ].map(({ label, value, setter }) => (
              <Grid item xs={12} sm={6} key={label}>
                <TextField
                  label={label}
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#FFFFFF",
          borderTop: `1px solid ${theme.customColors.border.subtle}`,
          px: 4,
          py: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            color: theme.customColors.text.primary,
            fontWeight: 500,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !validateFields() || loadingCustomerIntegrations || submitting
          }
          startIcon={<CheckCircleIcon />}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "white",
            textTransform: "none",
            px: 4,
            py: 1.5,
            fontWeight: 600,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: theme.customColors.button.hover,
            },
            "&:disabled": {
              backgroundColor: theme.customColors.background.disabled,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {submitting ? "Connecting..." : "Connect Integration"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default AzureConnectionDrawer;
